import { motion } from "framer-motion";
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Building2,
  Truck,
  Home,
  Sparkles,
  Target,
  Users,
  Shield,
  Briefcase,
  Wrench,
} from "lucide-react";

const services = [
  {
    icon: DollarSign,
    title: "MCA (Merchant Cash Advance)",
    description:
      "Quick access to working capital based on your daily credit card sales. Flexible repayment through a percentage of your sales.",
  },
  {
    icon: TrendingUp,
    title: "Term Loan",
    description:
      "Traditional business loans with fixed monthly payments and competitive interest rates. Ideal for long-term investments and growth.",
  },
  {
    icon: Briefcase,
    title: "Line of Credit",
    description:
      "Revolving credit line that provides flexible access to funds whenever you need them. Pay interest only on what you use.",
  },
  {
    icon: Shield,
    title: "SBA Loans",
    description:
      "Government-backed loans offering lower down payments and longer repayment terms. Perfect for small business expansion.",
  },
  {
    icon: Truck,
    title: "Equipment Financing",
    description:
      "Finance the purchase of business equipment and machinery. The equipment serves as collateral with competitive rates.",
  },
  {
    icon: Building2,
    title: "Bridge Funding",
    description:
      "Short-term financing to bridge gaps between transactions. Quick approval for time-sensitive business opportunities.",
  },
  {
    icon: Home,
    title: "Real Estate Financing",
    description:
      "Commercial and residential real estate loans. Finance property purchases, refinancing, or development projects.",
  },
  {
    icon: CreditCard,
    title: "0% Interest Business Credit Cards",
    description:
      "Get business credit cards with 0% interest for up to 18 months. Build credit while managing cash flow efficiently.",
  },
  {
    icon: Sparkles,
    title: "Startup Funding",
    description:
      "Specialized funding solutions for new businesses. Turn your entrepreneurial vision into reality with the right capital.",
  },
  {
    icon: Target,
    title: "Consolidation",
    description:
      "Combine multiple business debts into one manageable payment. Simplify finances and potentially lower your interest rates.",
  },
  {
    icon: Users,
    title: "Personal Loans",
    description:
      "Personal funding solutions for individual needs. Quick approval with flexible terms and competitive rates.",
  },
  {
    icon: Wrench,
    title: "Credit Repair Services",
    description:
      "Professional credit repair to improve your credit score. Expert guidance to help you qualify for better financing options.",
  },
];

export default function ServicesSection() {
  return (
    <section className="py-24 px-6 md:px-12 relative" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-4xl md:text-6xl font-bold mb-4">
            <span className="gold-gradient-text" data-testid="services-heading">
              Our Services
            </span>
          </h2>
          <p className="font-manrope text-white/70 text-lg max-w-2xl mx-auto">
            Comprehensive funding solutions tailored to your business needs
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card p-8 rounded-xl hover:scale-102 transition-all duration-500 group"
                data-testid={`service-card-${index}`}
              >
                <div className="flex flex-col h-full">
                  <div className="mb-4">
                    <Icon
                      className="w-12 h-12 text-gold group-hover:scale-110 transition-transform duration-300"
                      strokeWidth={1.5}
                    />
                  </div>
                  <h3 className="font-playfair text-xl font-semibold text-white mb-3 group-hover:text-gold transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="font-manrope text-white/70 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}